// Design helpers from components/crm/ui.tsx, components/ui.tsx and
// components/TimeInput.tsx that ui.js does not have yet, plus the mapping of
// backend CRM rows (contacts, leads, lead tasks) to the design's vocabulary.
// Used by Contacts, Tasks, Scoring / Assignment rules, Tags and TaskDrawer.
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Check, ChevronDown, ChevronLeft, ChevronRight, Clock, Columns3, Eye, Facebook, Footprints, Globe,
  GripVertical, Instagram, Lock, MapPin, Megaphone, MessageCircle, PenLine, Pencil, Phone, RotateCcw, Trash2, UserPlus, X,
} from "lucide-react";
import config from "../../../config";
import { SearchSelect } from "../SearchSelect";
import { SearchBox, paper, useOutside } from "./ui";

/* =====================================================================
   API
   ===================================================================== */

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
/** GET → JSON body, or null when the call fails. */
export const apiGet = (path, params) => axios.get(`${config.API_BASE_URL}${path}`, { ...auth(), params }).then((r) => r.data).catch(() => null);
/** Any write. Rejects with the backend's message so callers can show it. */
export const apiCall = (method, path, data, params) =>
  axios({ method, url: `${config.API_BASE_URL}${path}`, data, params, ...auth() })
    .then((r) => r.data)
    .catch((e) => { throw new Error(e.response?.data?.message || "Something went wrong. Try again."); });

/**
 * GET a file (CSV export) and save it. Rejects with the backend's message; an
 * error body arrives as a Blob here, so it is read back as JSON.
 */
export async function downloadFile(path, params, filename) {
  try {
    const r = await axios.get(`${config.API_BASE_URL}${path}`, { ...auth(), params, responseType: "blob" });
    const name = /filename="?([^";]+)"?/i.exec(r.headers?.["content-disposition"] || "")?.[1] || filename;
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) {
    let message = "Couldn't download the file. Try again.";
    try { const body = JSON.parse(await e.response.data.text()); if (body?.message) message = body.message; } catch (_) { /* not JSON */ }
    throw new Error(message);
  }
}

/** The signed-in user (users.id), from the login response. */
export const currentUser = () => {
  try { return JSON.parse(localStorage.getItem("user") || "{}") || {}; } catch (_) { return {}; }
};

/** People who can own leads and tasks (GET /api/staff/assignable-users): { id: users.id, staffId, name }. */
let peopleCache = null;
export function loadPeople() {
  if (!peopleCache) {
    peopleCache = apiGet("/api/staff/assignable-users").then((r) => {
      if (!r?.success) { peopleCache = null; return []; }
      return (r.data || []).map((u) => ({ id: u.user_id, staffId: u.staff_id || null, name: u.name || u.email || "Unnamed" }));
    });
  }
  return peopleCache;
}
export function usePeople() {
  const [people, setPeople] = useState([]);
  useEffect(() => { let live = true; loadPeople().then((list) => live && setPeople(list)); return () => { live = false; }; }, []);
  return people;
}

/** Every page of a paged list endpoint (limit 100), up to `maxPages`. */
export async function fetchAllPages(path, params = {}, maxPages = 30) {
  const out = [];
  for (let page = 1; page <= maxPages; page += 1) {
    const r = await apiGet(path, { ...params, page, limit: 100 });
    if (!r?.success) return page === 1 ? null : out;
    out.push(...(r.data || []));
    if (page >= (r.pagination?.pages || 1)) break;
  }
  return out;
}

/* =====================================================================
   Formats (design: state/crm.tsx)
   ===================================================================== */

export const fullName = (c) => `${c.firstName || ""} ${c.lastName || ""}`.trim();
export const initials = (c) => `${(c.firstName || "")[0] ?? ""}${(c.lastName || "")[0] ?? ""}`.toUpperCase();
export const initialsFor = (name = "") => name.split(/\s+/).map((part) => part[0] ?? "").slice(0, 2).join("").toUpperCase();
/** 10-digit Indian number from whatever the backend stored. */
export const phoneDigits = (p) => {
  let d = String(p || "").replace(/\D/g, "");
  if (d.length === 12 && d.startsWith("91")) d = d.slice(2);
  return d;
};
export const fmtPhone = (p) => {
  if (!p) return "";
  const d = phoneDigits(p);
  return d.length === 10 ? `+91 ${d.slice(0, 5)} ${d.slice(5)}` : String(p);
};
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const fmtShort = (ts) => { const d = new Date(ts); return `${d.getDate()} ${MONTHS[d.getMonth()]}`; };
export const fmtTime = (ts) => { const d = new Date(ts); const h = d.getHours(); return `${h % 12 || 12}:${String(d.getMinutes()).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`; };
export const fmtDateTime = (ts) => `${fmtShort(ts)}, ${fmtTime(ts)}`;
export const DAY = 86_400_000;
export function fmtDue(ts) {
  const now = Date.now();
  const today = new Date(now).setHours(0, 0, 0, 0);
  const d = Math.floor((ts - today) / DAY);
  if (ts < now && d < 0) return d === -1 ? "Overdue since yesterday" : `Overdue by ${-d} days`;
  if (d === 0) return ts < now ? `Overdue, today ${fmtTime(ts)}` : `Today, ${fmtTime(ts)}`;
  if (d === 1) return `Tomorrow, ${fmtTime(ts)}`;
  return fmtDateTime(ts);
}
/** "YYYY-MM-DD" (or an ISO timestamp the backend made from a DATE) → local midnight. */
export function localDay(value) {
  if (!value) return null;
  const s = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d).getTime(); }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}
export const dateValue = (ts) => { const d = new Date(ts); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
export const timeValue = (ts) => { const d = new Date(ts); return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`; };

/** How a lead's numeric id is shown ("L-1042", as in the design). */
export const leadCode = (id) => `#${id}`;

/* =====================================================================
   Backend ↔ design vocabularies
   ===================================================================== */

export const SOURCE_LABEL = {
  manual: "Manual entry", meta_facebook: "Facebook", meta_instagram: "Instagram", meta_leads: "Meta lead ads",
  google_business: "Google Business", google_ads: "Google Ads", whatsapp: "WhatsApp", website_form: "Website form",
  referral: "Referral", walk_in: "Walk-in", phone_call: "Phone call", other: "Other",
};
/** Design's lead sources, in the design's order, with the backend value each one saves as. */
export const SOURCES = ["Meta lead ads", "Facebook", "Instagram", "Website form", "Google Business", "WhatsApp", "Walk-in", "Referral", "Manual entry"];
export const SOURCE_KEY = Object.fromEntries(Object.entries(SOURCE_LABEL).map(([k, v]) => [v, k]));
export const sourceLabel = (key) => SOURCE_LABEL[key] || key || "";

export const PRIORITIES = ["High", "Medium", "Low"];
export const priorityLabel = (key) => (key === "urgent" || key === "high" ? "High" : key === "low" ? "Low" : "Medium");

export const CONTACT_TYPES = ["Individual", "Business"];
export const CONTACT_STATUSES = ["Active", "Inactive"];
const TYPE_LABEL = { individual: "Individual", corporate: "Business", vendor: "Vendor", partner: "Partner", other: "Other" };
export const contactTypeLabel = (key) => TYPE_LABEL[key] || "Individual";
export const contactTypeKey = (label) => Object.keys(TYPE_LABEL).find((k) => TYPE_LABEL[k] === label) || "individual";
export const TITLES = ["Mr.", "Ms.", "Mrs.", "Dr.", "Master", "Baby"];

/** contacts row → the design's Contact. The backend keeps one address text; line 2 (area) is its second line. */
export function toContact(row) {
  const [line1 = "", ...rest] = String(row.address || "").split("\n");
  return {
    id: row.id,
    title: row.title || "",
    firstName: row.first_name || "",
    lastName: row.last_name || "",
    email: row.email || "",
    phone: phoneDigits(row.phone),
    rawPhone: row.phone || "",
    altPhone: phoneDigits(row.alternate_phone),
    typeKey: row.contact_type || "individual",
    type: contactTypeLabel(row.contact_type),
    status: row.status === "inactive" ? "Inactive" : "Active",
    company: row.company_name || "",
    jobTitle: row.job_title || "",
    website: row.website || "",
    linkedin: row.linkedin || "",
    tags: (row.tags || []).map((t) => t.name),
    tagRows: row.tags || [],
    address: { line1, line2: rest.join(" "), city: row.city || "", state: row.state || "", pin: row.pincode || "", country: row.country || "" },
    notes: row.notes || "",
    createdAt: row.created_at ? new Date(row.created_at).getTime() : null,
    createdBy: row.created_by_name || "",
    // The list's lead summary (GET /api/contacts, item 154): null when the row
    // doesn't carry it (a single GET /api/contacts/:id).
    leadCount: row.lead_count === undefined || row.lead_count === null ? null : Number(row.lead_count) || 0,
    latestLead: row.latest_lead || null,
    // Contact owner (contacts.assigned_to is a staff_members.id) and the
    // activity logged on the contact itself (GET /api/contacts/:id only).
    ownerStaffId: row.assigned_to || null,
    ownerName: row.assigned_to_name || "",
    activities: Array.isArray(row.activities) ? row.activities : [],
  };
}

/* ---------- Leads ---------- */

const STATUS_FROM_KEY = { new: "New", contacted: "Contacted", qualified: "Qualified", proposal: "Proposal", negotiation: "Negotiation", won: "Converted", lost: "Lost", junk: "Junk" };
export const OPEN_LEAD = (row) => !["won", "lost", "junk"].includes(row.status);
/** leads row → what the CRM screens show. */
export function toLead(row, statusLabels = {}) {
  const key = row.status_key || row.status;
  return {
    id: row.id,
    code: leadCode(row.id),
    contactId: row.contact_id,
    status: STATUS_FROM_KEY[key] || statusLabels[key] || STATUS_FROM_KEY[row.status] || "New",
    open: OPEN_LEAD(row),
    source: sourceLabel(row.source),
    service: row.interested_service || "",
    facility: row.facility_name || "",
    assignee: row.assigned_to_name || "",
    phone: phoneDigits(row.phone),
    firstName: row.first_name || "",
    lastName: row.last_name || "",
    createdAt: row.created_at ? new Date(row.created_at).getTime() : 0,
    raw: row,
  };
}
/** A contact's leads, newest first (GET /api/leads?contact_id=, item 153); null when the call fails. */
export async function leadsOfContact(contact, params = {}) {
  if (!contact?.id) return [];
  const r = await apiGet("/api/leads", { ...params, contact_id: contact.id, limit: 200, sort_by: "created_at", sort_order: "DESC" });
  if (!r?.success) return null;
  return r.data || [];
}
/** Several contacts' leads in one request: { [contactId]: rows }, or null when the call fails. */
export async function leadsOfContacts(ids, params = {}) {
  const list = [...new Set(ids.filter(Boolean))];
  if (!list.length) return {};
  const r = await apiGet("/api/leads", { ...params, contact_id: list.join(","), limit: 1000, sort_by: "created_at", sort_order: "DESC" });
  if (!r?.success) return null;
  const out = Object.fromEntries(list.map((id) => [id, []]));
  (r.data || []).forEach((l) => { (out[l.contact_id] = out[l.contact_id] || []).push(l); });
  return out;
}

/* ---------- Tasks ---------- */

export const TASK_TYPES = ["Call", "WhatsApp", "Follow-up", "Meeting", "Email"];
export const TASK_STATUSES = ["To do", "In progress", "Done", "Cancelled"];
const TYPE_FROM_KEY = { follow_up_call: "Call", callback: "Call", follow_up_email: "Email", whatsapp: "WhatsApp", meeting: "Meeting", demo: "Meeting", site_visit: "Meeting" };
export const taskTypeLabel = (key) => TYPE_FROM_KEY[key] || "Follow-up";
/** Backend task_type for a design type (WhatsApp: item 159). */
export const TASK_TYPE_KEY = { Call: "follow_up_call", Email: "follow_up_email", WhatsApp: "whatsapp", Meeting: "meeting", "Follow-up": "other" };
const TASK_STATUS_FROM_KEY = { pending: "To do", overdue: "To do", in_progress: "In progress", completed: "Done", cancelled: "Cancelled" };
export const TASK_STATUS_KEY = { "To do": "pending", "In progress": "in_progress", Done: "completed", Cancelled: "cancelled" };
export const PRIORITY_KEY = { High: "high", Medium: "medium", Low: "low" };

/** lead_tasks row → the design's Task. A task with no time is due at the end of its day. */
export function toTask(row) {
  const day = localDay(row.due_date);
  const [h, m] = String(row.due_time || "").split(":").map(Number);
  const hasTime = !!row.due_time && Number.isFinite(h);
  const due = day === null ? Date.now() : hasTime ? day + (h * 60 + (m || 0)) * 60_000 : day + DAY - 60_000;
  return {
    id: row.id,
    leadId: row.lead_id,
    title: row.title || "",
    type: taskTypeLabel(row.task_type),
    typeKey: row.task_type,
    priority: priorityLabel(row.priority),
    priorityKey: row.priority,
    status: TASK_STATUS_FROM_KEY[row.status] || "To do",
    due,
    hasTime,
    assigneeId: row.assigned_to || null,
    assignee: row.assigned_to_name || "",
    notes: row.description || "",
    // What was written when it was marked complete (CompleteTaskDialog).
    completionNotes: row.completion_notes || "",
    completedAt: row.completed_at ? new Date(row.completed_at).getTime() : null,
    createdAt: row.created_at ? new Date(row.created_at).getTime() : null,
    createdBy: row.created_by_name || "",
    leadName: [row.lead_first_name, row.lead_last_name].filter(Boolean).join(" "),
    leadPhone: phoneDigits(row.lead_phone),
    raw: row,
  };
}

/* =====================================================================
   Design components missing from ui.js
   ===================================================================== */

export const inputCls =
  "h-11 w-full rounded-sm border border-line bg-paper px-3.5 text-small text-ink outline-none placeholder:text-muted/70 hover:border-muted/50 focus:border-ink focus:ring-2 focus:ring-ink/10";

const TASK_STYLE = {
  "To do": "bg-mist text-ink",
  "In progress": "bg-sky-soft text-ink",
  Done: "bg-success-soft text-success",
  Cancelled: "bg-mist text-muted line-through",
};
export function TaskStatusBadge({ status }) {
  return <span className={`inline-flex h-6 items-center whitespace-nowrap rounded-full px-2.5 text-caption font-semibold ${TASK_STYLE[status] || TASK_STYLE["To do"]}`}>{status}</span>;
}

export { PriorityBadge } from "./ui";

const SOURCE_ICON = {
  "Meta lead ads": Megaphone, Facebook, Instagram, "Website form": Globe, "Google Business": MapPin, "Google Ads": Megaphone,
  WhatsApp: MessageCircle, "Walk-in": Footprints, Referral: UserPlus, "Manual entry": PenLine, "Phone call": Phone, Other: Globe,
};
export function SourceIcon({ source, size = 14 }) {
  const Icon = SOURCE_ICON[source] || PenLine;
  return <Icon size={size} strokeWidth={1.75} />;
}
export function SourceLabel({ source }) {
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap text-small">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-mist text-ink">
        <SourceIcon source={source} size={13} />
      </span>
      {source}
    </span>
  );
}

export function FormSection({ icon, title, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className={`${paper} overflow-hidden`}>
      <button type="button" aria-expanded={open} onClick={() => setOpen((o) => !o)} className="flex h-14 w-full items-center gap-3 px-6 text-left hover:bg-mist/50">
        <span className="text-ink">{icon}</span>
        <span className="flex-1 text-body font-semibold">{title}</span>
        <ChevronDown size={18} strokeWidth={1.75} className={`text-muted transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={`grid transition-[grid-template-rows] duration-250 ease-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
        <div className="overflow-hidden">
          <div className="grid grid-cols-2 gap-x-5 gap-y-4 border-t border-line px-6 pb-6 pt-5">{children}</div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Columns ---------- */

/**
 * A user's saved table layout (GET/PUT /api/view-preferences/:viewKey). The
 * backend replaces the whole row on save, so `save(patch)` sends every field;
 * visible_columns null means "the screen's defaults".
 */
export function useSavedView(viewKey) {
  const [pref, setPref] = useState(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let live = true;
    apiGet(`/api/view-preferences/${viewKey}`).then((r) => { if (!live) return; setPref(r?.success ? r.data : null); setLoaded(true); });
    return () => { live = false; };
  }, [viewKey]);
  const save = (patch) => {
    const next = { visible_columns: null, sort_by: "created_at", sort_order: "DESC", page_size: 25, ...(pref || {}), ...patch };
    setPref(next);
    apiCall("put", `/api/view-preferences/${viewKey}`, {
      visible_columns: next.visible_columns && next.visible_columns.length ? next.visible_columns : null,
      sort_by: next.sort_by, sort_order: next.sort_order, page_size: next.page_size,
    }).catch(() => {});
  };
  return { pref, loaded, save };
}

/**
 * Column choice for a table. With `remote` ({ ids, save }) it is the user's
 * saved view (contacts_table, item 170); without, it is kept in this browser.
 */
export function useColumns(all, storageKey, remote) {
  const defaults = useMemo(() => all.filter((c) => c.default || c.locked).map((c) => c.id), [all]);
  const [stored, setStored] = useState(() => {
    try { const v = JSON.parse(localStorage.getItem(storageKey) || "null"); return Array.isArray(v) ? v : undefined; } catch (_) { return undefined; }
  });
  const save = (ids) => {
    if (remote) { remote.save(ids); return; }
    setStored(ids); try { localStorage.setItem(storageKey, JSON.stringify(ids)); } catch (_) { /* private mode */ }
  };
  const known = (list) => { const kept = (list || []).filter((id) => all.some((c) => c.id === id)); return kept.length ? kept : null; };
  const ids = (remote ? known(remote.ids) : stored) ?? defaults;
  const visible = ids.map((id) => all.find((c) => c.id === id)).filter(Boolean);
  return {
    visible,
    ids,
    toggle: (id) => save(ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]),
    move: (id, dir) => {
      const i = ids.indexOf(id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= ids.length) return;
      const next = [...ids];
      [next[i], next[j]] = [next[j], next[i]];
      save(next);
    },
    place: (id, to) => {
      const next = ids.filter((x) => x !== id);
      next.splice(Math.max(0, Math.min(to, next.length)), 0, id);
      if (next.join() !== ids.join()) save(next);
    },
    reset: () => save(defaults),
    isDefault: ids.join() === defaults.join(),
  };
}

/** "Columns" button: tick to show, arrows to reorder, reset to default. */
export function ColumnPicker({ all, cols, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [q, setQ] = useState("");
  const [dragId, setDragId] = useState(null);
  const ref = useOutside(() => setOpen(false), open);
  const shown = cols.ids.map((id) => all.find((c) => c.id === id)).filter(Boolean);
  const hidden = all.filter((c) => !cols.ids.includes(c.id) && c.label.toLowerCase().includes(q.toLowerCase()));

  return (
    <div ref={ref} className="relative">
      <button aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen((o) => !o)} className="flex h-10 items-center gap-2 rounded-full bg-paper px-4 text-small font-medium ring-1 ring-line hover:ring-muted/50">
        <Columns3 size={16} strokeWidth={1.75} />
        Columns
        <span className="tabular-nums text-muted">{cols.ids.length}</span>
      </button>
      {open && (
        <div role="dialog" aria-label="Table columns" className="absolute right-0 top-12 z-30 flex max-h-[520px] w-[380px] animate-nav-in flex-col rounded-md bg-paper shadow-md ring-1 ring-line">
          <div className="flex items-center justify-between px-4 pb-2 pt-3.5">
            <span className="text-small font-semibold">Shown in table</span>
            {!cols.isDefault && (
              <button onClick={cols.reset} className="flex items-center gap-1 text-caption font-semibold text-sky-deep hover:underline">
                <RotateCcw size={12} strokeWidth={2} />
                Reset
              </button>
            )}
          </div>
          <ul className="flex flex-wrap gap-1.5 px-4 pb-3 pt-1" aria-label="Shown columns, drag to reorder">
            {shown.map((c, i) => (
              <li
                key={c.id}
                draggable
                tabIndex={0}
                aria-label={`${c.label}, position ${i + 1} of ${shown.length}. Use left and right arrow keys to move`}
                onDragStart={(e) => { setDragId(c.id); e.dataTransfer.effectAllowed = "move"; }}
                onDragOver={(e) => { e.preventDefault(); if (dragId && dragId !== c.id) cols.place(dragId, i); }}
                onDragEnd={() => setDragId(null)}
                onKeyDown={(e) => {
                  if (e.key === "ArrowLeft" && i > 0) cols.move(c.id, -1);
                  if (e.key === "ArrowRight" && i < shown.length - 1) cols.move(c.id, 1);
                  if ((e.key === "Delete" || e.key === "Backspace") && !c.locked) cols.toggle(c.id);
                }}
                className={`flex h-8 cursor-grab items-center gap-1 rounded-full pl-2 text-caption font-medium outline-none ring-1 focus-visible:ring-2 focus-visible:ring-ink/30 active:cursor-grabbing ${c.locked ? "bg-mist pr-3 ring-transparent" : "bg-paper pr-1 ring-line hover:ring-muted/50"} ${dragId === c.id ? "opacity-40" : ""}`}
              >
                <GripVertical size={12} strokeWidth={1.75} className="text-muted/60" />
                {c.label}
                {c.locked ? (
                  <Lock size={11} strokeWidth={2} className="ml-0.5 text-muted" aria-label="Always shown" />
                ) : (
                  <button type="button" tabIndex={-1} aria-label={`Remove ${c.label}`} title="Remove" onClick={() => cols.toggle(c.id)} className="flex h-6 w-6 items-center justify-center rounded-full text-muted hover:bg-mist hover:text-danger">
                    <X size={12} strokeWidth={2} />
                  </button>
                )}
              </li>
            ))}
          </ul>
          <p className="-mt-1 px-4 pb-1 text-caption text-muted">Drag to reorder</p>
          <div className="mt-2 flex flex-col gap-2 border-t border-line px-4 pb-2 pt-3">
            <span className="text-small font-semibold">Add a column</span>
            <SearchBox value={q} onChange={setQ} placeholder="Find a field" className="w-full" />
          </div>
          <ul className="flex min-h-0 flex-1 flex-col overflow-y-auto px-1.5 pb-2">
            {hidden.length === 0 && <li className="px-3 py-3 text-caption text-muted">{q ? `No field called "${q}"` : "All fields are shown"}</li>}
            {hidden.map((c) => (
              <li key={c.id}>
                <button onClick={() => cols.toggle(c.id)} className="flex h-9 w-full items-center justify-between rounded-xs px-3 text-left text-small hover:bg-mist">
                  {c.label}
                  <span className="text-caption font-semibold text-sky-deep">Add</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/* ---------- Detail page parts ---------- */

/** Label over value. Empty values show a quiet "Not added" so the grid stays even. */
export function KV({ label, children, mono }) {
  const empty = children === "" || children === null || children === undefined;
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <dt className="text-caption text-muted">{label}</dt>
      <dd className={`min-w-0 break-words text-small ${mono ? "font-mono text-[13px]" : ""} ${empty ? "text-muted/60" : "text-ink"}`}>{empty ? "Not added" : children}</dd>
    </div>
  );
}

export function SectionCard({ title, action, children, className = "" }) {
  return (
    <section aria-label={title} className={`${paper} flex flex-col gap-5 p-6 ${className}`}>
      <div className="flex min-h-9 items-center justify-between gap-4">
        <h2 className="text-h3 font-semibold">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

/** Page staggering helper: `style={reveal(i)}` with `animate-nav-in` */
export const reveal = (i) => ({ animationDelay: `${40 + i * 55}ms` });

/** View, edit, delete as quiet icon buttons. Same order in every table. Leave out onEdit / onDelete to hide them (view only). */
export function RowActions({ label, onView, onEdit, onDelete }) {
  const btn = "flex h-9 w-9 items-center justify-center rounded-full text-muted transition-colors duration-150 hover:bg-paper hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/20";
  return (
    <span className="flex items-center gap-0.5">
      <button type="button" onClick={onView} aria-label={`View ${label}`} title="View" className={btn}><Eye size={16} strokeWidth={1.75} /></button>
      {onEdit && <button type="button" onClick={onEdit} aria-label={`Edit ${label}`} title="Edit" className={btn}><Pencil size={16} strokeWidth={1.75} /></button>}
      {onDelete && <button type="button" onClick={onDelete} aria-label={`Delete ${label}`} title="Delete" className={`${btn} hover:text-danger`}><Trash2 size={16} strokeWidth={1.75} /></button>}
    </span>
  );
}

/* ---------- Pagination ---------- */

export const PAGE_SIZES = [25, 50, 100];

/** Page state for a list filtered here (design: usePagination). Back to page 1 when `resetKey` changes. */
export function usePagination(total, resetKey, initialSize = 25) {
  const [page, setPage] = useState(1);
  const [size, setSizeState] = useState(initialSize);
  const pages = Math.max(1, Math.ceil(total / size));
  useEffect(() => setPage(1), [resetKey]);
  useEffect(() => { if (page > pages) setPage(pages); }, [page, pages]);
  const current = Math.min(page, pages);
  return { page: current, pages, size, start: (current - 1) * size, end: Math.min(total, current * size), total, setPage, setSize: (n) => { setSizeState(n); setPage(1); } };
}

function pageList(page, pages) {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
  const out = [1];
  const from = Math.max(2, page - 1);
  const to = Math.min(pages - 1, page + 1);
  if (from > 2) out.push("gap");
  for (let i = from; i <= to; i++) out.push(i);
  if (to < pages - 1) out.push("gap");
  out.push(pages);
  return out;
}

/** "1 to 25 of 164 leads" · rows per page · page buttons. Scrolls the list back into view on page change. */
export function Pagination({ state, noun, plural, scrollTo }) {
  const { page, pages, size, start, end, total, setPage, setSize } = state;
  if (total === 0) return null;
  const go = (p) => {
    setPage(p);
    const el = scrollTo?.current;
    if (el && el.getBoundingClientRect().top < 0) el.scrollIntoView({ block: "start", behavior: "smooth" });
  };
  const btn = "flex h-9 min-w-9 items-center justify-center rounded-full px-2 text-small tabular-nums outline-none focus-visible:ring-2 focus-visible:ring-ink/20";
  return (
    <nav aria-label="Pagination" className="flex flex-wrap items-center justify-between gap-3 px-1 py-2 text-small">
      <div className="flex items-center gap-4 text-muted">
        <span className="tabular-nums">
          <span className="font-semibold text-ink">{(start + 1).toLocaleString("en-IN")} to {end.toLocaleString("en-IN")}</span>{" "}
          of {total.toLocaleString("en-IN")} {total === 1 ? noun : plural ?? `${noun}s`}
        </span>
        <label className="flex items-center gap-2">
          Rows per page
          <select value={size} onChange={(e) => setSize(Number(e.target.value))} className="h-9 rounded-full bg-paper pl-3 pr-2 text-small font-medium text-ink outline-none ring-1 ring-line hover:ring-muted/50 focus-visible:ring-2 focus-visible:ring-ink/20">
            {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
      </div>
      {pages > 1 && (
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => go(page - 1)} disabled={page === 1} aria-label="Previous page" className={`${btn} text-ink hover:bg-paper disabled:pointer-events-none disabled:opacity-35`}>
            <ChevronLeft size={16} strokeWidth={1.75} />
          </button>
          {pageList(page, pages).map((p, i) =>
            p === "gap" ? (
              <span key={`g${i}`} className="px-1 text-muted" aria-hidden>...</span>
            ) : (
              <button key={p} type="button" onClick={() => go(p)} aria-current={p === page ? "page" : undefined} aria-label={`Page ${p}`} className={`${btn} ${p === page ? "bg-ink font-semibold text-paper" : "text-ink hover:bg-paper"}`}>
                {p}
              </button>
            ),
          )}
          <button type="button" onClick={() => go(page + 1)} disabled={page === pages} aria-label="Next page" className={`${btn} text-ink hover:bg-paper disabled:pointer-events-none disabled:opacity-35`}>
            <ChevronRight size={16} strokeWidth={1.75} />
          </button>
        </div>
      )}
    </nav>
  );
}

/* ---------- Form controls (design: components/ui.tsx, TimeInput.tsx) ---------- */

export function Checkbox({ id, checked, onChange, children, invalid }) {
  return (
    <label htmlFor={id} className="flex cursor-pointer items-start gap-3 text-small text-ink">
      <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="peer sr-only" />
      <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-xs border peer-focus-visible:ring-2 peer-focus-visible:ring-ink/20 ${checked ? "border-ink bg-ink text-paper" : invalid ? "border-danger bg-paper" : "border-muted/50 bg-paper"}`}>
        {checked && <Check size={14} strokeWidth={2.5} />}
      </span>
      <span>{children}</span>
    </label>
  );
}

export function GhostButton({ children, className = "", ...props }) {
  return (
    <button {...props} className={`flex h-12 items-center justify-center gap-2 rounded-full px-5 text-body font-semibold text-ink hover:bg-mist ${className}`}>
      {children}
    </button>
  );
}

/** "14:30" → "2:30 PM". */
export function to12h(hhmm) {
  const [h, m] = hhmm.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
}

/** 12-hour time picker; value stays "HH:MM". */
export function TimeInput({ id, value, onChange, step = 15, min = "00:00", max = "23:59", invalid, disabled, size = "md", placeholder = "Choose time", ariaLabel, className = "" }) {
  const options = useMemo(() => {
    const toMin = (t) => { const [h, m] = t.split(":").map(Number); return h * 60 + m; };
    const list = [];
    for (let t = toMin(min); t <= toMin(max); t += step) list.push(`${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`);
    if (value && !list.includes(value)) list.push(value);
    list.sort();
    // The small size drops the clock icon: in a narrow row (a shift on a phone) it pushed "9:00 AM" to "9:0…".
    return list.map((t) => ({ value: t, label: to12h(t), ...(size === "sm" ? {} : { leading: <Clock size={14} strokeWidth={1.75} className="shrink-0 text-muted" /> }) }));
  }, [step, min, max, value, size]);
  return (
    <SearchSelect id={id} value={value} options={options} onChange={onChange} searchable searchPlaceholder="Type a time, like 7:30 PM" placeholder={placeholder} invalid={invalid} disabled={disabled} size={size} ariaLabel={ariaLabel} className={className} emptyText="No time matches. Try 9:00 AM." />
  );
}
